// src/components/chat/ChatWindow.tsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ChatHeader from './ChatHeader';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import { useConnection } from '../../hooks/useConnection';
import { useConnectionStore } from '../../store/connectionStore';
import { Message, Conversation } from '../../types/chat';
import { BlockUserModal } from '../chat/modals/BlockUserModal';
import { BlockSuccess } from '../chat/modals/BlockSuccess';
import { ReportUserModal } from '../chat/modals/ReportUserModal';
import { ReportSuccess } from '../chat/modals/ReportSuccess';
import { supabase } from '../../lib/supabase';
import { cn } from '@/lib/utils';

interface ChatWindowProps {
  conversation?: Conversation;
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  messageInput: string;
  setMessageInput: (value: string) => void;
  isTyping: boolean;
  isUploadingImage?: boolean;
  onSendMessage: (conversationId: string, replyToMessageId?: string | null) => void;
  onImageUpload?: (imageUrl: string) => void;
  onMarkAsRead: (messageId: string) => void;
  onEditMessage?: (messageId: string, newContent: string) => void;
  onDeleteMessage?: (messageId: string) => void;
  onLikeMessage?: (messageId: string) => void;
  onReplyToMessage?: (message: Message | null) => void;
  /** New: called when user scrolls to top to load older messages */
  onLoadPreviousMessages?: (conversationId: string) => Promise<void>;
  editingMessageId?: string | null;
  setEditingMessageId?: (messageId: string | null) => void;
  replyingTo?: Message | null;
  onBack: () => void;
  className?: string;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  conversation,
  messages,
  isLoading,
  error,
  messageInput,
  setMessageInput,
  isTyping,
  isUploadingImage,
  onSendMessage,
  onImageUpload,
  onMarkAsRead,
  onEditMessage,
  onDeleteMessage,
  onLikeMessage,
  onReplyToMessage,
  onLoadPreviousMessages,
  editingMessageId,
  setEditingMessageId,
  replyingTo,
  onBack,
  className,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isLoadingMore = useRef(false);

  // **Infinite‐scroll:** load older messages when scrolled **near** the top
  const handleScroll = async () => {
    const el = scrollRef.current;
    if (
      !el ||
      !conversation?.id ||
      !onLoadPreviousMessages ||
      isLoadingMore.current
    ) return;

    // instead of === 0, trigger when within 20px of the top
    if (el.scrollTop <= 20) {
      isLoadingMore.current = true;
      const prevHeight = el.scrollHeight;

      await onLoadPreviousMessages(conversation.id);

      // restore scroll position so content doesn’t jump
      setTimeout(() => {
        const newHeight = el.scrollHeight;
        el.scrollTop = newHeight - prevHeight;
      }, 30);
    }
  };

  // **Auto-scroll to bottom** when genuinely new messages arrive
  useEffect(() => {
    // if we just loaded history, skip snapping to bottom
    if (isLoadingMore.current) {
      isLoadingMore.current = false;
      return;
    }

    const el = scrollRef.current;
    if (!el) return;

    const t = setTimeout(() => {
      el.scrollTop = el.scrollHeight;
    }, 30);

    return () => clearTimeout(t);
  // key off the last message’s unique ID, not the array length
  }, [messages[messages.length - 1]?.id]);

  // Un-stick scroll after like/tap
  const forceScrollRefresh = () => {
    const el = scrollRef.current;
    if (!el) return;
    const curr = el.scrollTop;
    el.scrollTop = curr + 1;
    requestAnimationFrame(() => {
      el.scrollTop = curr;
    });
  };

  // … all your existing connection/modals/back‐button logic remains unchanged …

  const navigate = useNavigate();
  const location = useLocation();
  const [showMenu, setShowMenu] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showBlockSuccess, setShowBlockSuccess] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showReportSuccess, setShowReportSuccess] = useState(false);
  const { connections } = useConnectionStore();
  const { handleSendRequest } = useConnection();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const connection = React.useMemo(() => {
    if (!conversation?.otherUserId) return null;
    return connections.find(
      c => c.other_user?.id === conversation.otherUserId
    );
  }, [connections, conversation?.otherUserId]);

  useEffect(() => {
    setIsPending(connection?.status === 'pending' || false);
  }, [connection?.status]);

  const handleConnect = async () => {
    if (!conversation?.otherUserId || !conversation?.name) return;
    setIsConnecting(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .select('auth_id')
        .eq('id', conversation.otherUserId)
        .single();
      if (error || !data) return;
      const success = await handleSendRequest(
        data.auth_id,
        conversation.name
      );
      if (success) setIsPending(true);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleBackClick = () => {
    if (location.state?.from === 'profile') {
      navigate(`/dashboard/members/${conversation.otherUserId}`);
    } else {
      onBack();
    }
  };

  if (!conversation) {
    return (
      <div className="h-full flex items-center justify-center text-dark-800">
        Select a conversation to start messaging
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative h-full flex flex-col',
        'bg-white dark:bg-dark-200',
        "bg-[url('/white_chatback.png')] dark:bg-[url('/dark_chatback.png')]",
        'bg-repeat bg-[length:400px_auto]',
        'before:absolute before:inset-0',
        'before:bg-white/45 dark:before:bg-dark-200/65',
        'before:pointer-events-none',
        'pb-safe',
        className
      )}
    >
      {/* Header */}
      <div
        className={cn(
          'flex-shrink-0 border-b border-gray-200 dark:border-dark-300 relative z-10 bg-white/50 dark:bg-dark-200/50 backdrop-blur-sm',
          'lg:relative'
        )}
      >
        <ChatHeader
          conversation={{
            id: conversation.otherUserId,
            name: conversation.name,
            image: conversation.image,
            location: conversation.city,
            online: false,
          }}
          isConnected={connection?.status === 'accepted'}
          isPending={isPending}
          onConnect={handleConnect}
          isConnecting={isConnecting}
          showMenu={showMenu}
          onMenuToggle={() => setShowMenu(p => !p)}
          onBackClick={handleBackClick}
          onBlockUser={() => setShowBlockModal(true)}
          onReportUser={() => setShowReportModal(true)}
        />
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scroll-smooth"
        style={{
          WebkitOverflowScrolling: 'touch',
          touchAction: 'manipulation',
        }}
        onScroll={handleScroll}
      >
        <MessageList
          messages={messages}
          isTyping={isTyping}
          typingName={conversation.name}
          conversation={conversation}
          onEdit={onEditMessage}
          onDelete={onDeleteMessage}
          onLike={id => {
            onLikeMessage?.(id);
            setTimeout(forceScrollRefresh, 50);
          }}
          editingId={editingMessageId}
          onStartEdit={setEditingMessageId!}
          onReply={onReplyToMessage}
        />
      </div>

      {/* Input */}
      <div
        className={cn(
          'flex-shrink-0 border-t border-gray-200 dark:border-dark-300 mt-auto relative z-10 bg-white/50 dark:bg-dark-200/50 backdrop-blur-sm',
          'lg:relative'
        )}
      >
        <MessageInput
          message={messageInput}
          onMessageChange={setMessageInput}
          onImageUpload={onImageUpload}
          onSend={() =>
            onSendMessage(conversation.id, replyingTo?.id ?? null)
          }
          replyingTo={replyingTo}
          onCancelReply={() => onReplyToMessage?.(null)}
        />
      </div>

      {/* Modals */}
      <BlockUserModal
        isOpen={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        onConfirm={() => setShowBlockSuccess(true)}
        username={conversation.name}
      />
      <BlockSuccess
        isOpen={showBlockSuccess}
        onClose={() => setShowBlockSuccess(false)}
        username={conversation.name}
      />
      <ReportUserModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        onSubmit={() => setShowReportSuccess(true)}
        username={conversation.name}
      />
      <ReportSuccess
        isOpen={showReportSuccess}
        onClose={() => setShowReportSuccess(false)}
        username={conversation.name}
      />
    </div>
  );
};

export default ChatWindow;
